import React from 'react'

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination,
    makeStyles,
    Theme,
    createStyles,
    Checkbox,
    IconButton,
} from '@material-ui/core'
import { MoreVert } from '@material-ui/icons'

import { InternalInvoice } from '../../models'
import { currencyFormatter } from '../../helpers'
import { InvoiceStatusBadge } from '../InternalInvoice'
import { formatDate } from '../InternalInvoice/InvoiceDetailModal/helpers'

interface HeadCell {
    id: keyof InternalInvoice
    label: string
    numeric: boolean
    visible?: boolean
}

interface InvoiceTableProps {
    invoices: InternalInvoice[] | undefined
    orderBy: keyof InternalInvoice
    order: SortOrder
    onRequestSort: (property: keyof InternalInvoice, order: SortOrder) => void
    onViewInvoice: (invoice: InternalInvoice) => void
    tableHeight: string
    page: number
    rowsPerPage: number
    totalCount: number
    onPageChange: (event: unknown, newPage: number) => void
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    selected: string[]
    onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void
    onSelectOne: (id: string) => void
    allPagesSelected?: boolean
    onSelectAllPages: () => void
    isDashboardOpen?: boolean
    visibleColumns?: Array<keyof InternalInvoice>
    hideSelectionColumn?: boolean
}

// const paginationHeight = 94

const defaultHeadCells: HeadCell[] = [
    { id: 'invoice_number', label: 'Invoice #', numeric: false },
    {
        id: 'billing_group_display_name',
        label: 'Billing Group',
        numeric: false,
    },
    {
        id: 'billing_email',
        label: 'Invoice Email',
        numeric: false,
    },
    { id: 'status', label: 'Status', numeric: false },
    { id: 'issue_date', label: 'Issue Date', numeric: false },
    { id: 'due_date', label: 'Due Date', numeric: false },
    { id: 'total_amount', label: 'Billed', numeric: true },
    { id: 'balance_due', label: 'Balance Due', numeric: true },
]

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: (props: { tableHeight: string }) => props.tableHeight,
            minHeight: (props: { tableHeight: string }) => props.tableHeight,
        },
        container: {
            overflow: 'auto',
        },
        headerCell: {
            backgroundColor: theme.palette.background.paper,
            fontWeight: 'bold',
        },
        voidedRow: {
            backgroundColor: theme.palette.action.disabledBackground,
            textDecoration: 'line-through',
        },
        normalRow: {
            backgroundColor: 'inherit',
            textDecoration: 'none',
        },
        pagination: {
            borderTop: `1px solid ${theme.palette.divider}`,
            marginTop: theme.spacing(1),
            minHeight: 60,
        },
        statusChip: {
            fontWeight: 500,
            minWidth: 100,
            '& .MuiIcon-root': {
                color: 'inherit',
            },
            '& .MuiSvgIcon-root': {
                color: 'inherit',
            },
        },
        statusIcon: {
            width: 16,
            height: 16,
            color: 'inherit',
        },
    }),
)

export const InvoiceTable: React.FC<InvoiceTableProps> = ({
    invoices,
    orderBy,
    order,
    onRequestSort,
    onViewInvoice,
    tableHeight,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    selected,
    onSelectAll,
    onSelectOne,
    allPagesSelected = false,
    onSelectAllPages,
    visibleColumns = defaultHeadCells.map((cell) => cell.id),
    hideSelectionColumn,
}) => {
    const classes = useStyles({ tableHeight })

    const createSortHandler = (property: keyof InternalInvoice) => () => {
        // If we're already sorting by this field, toggle direction
        // Otherwise, start with ascending
        const newOrder =
            orderBy === property && order === 'asc' ? 'desc' : 'asc'
        onRequestSort(property, newOrder)
    }

    const invoiceLength = invoices?.length ?? 0
    const currentPageInvoiceIds = invoices?.map((invoice) => invoice.id) ?? []

    // Calculate selection state for current page
    const currentPageSelectedCount = currentPageInvoiceIds.filter((id) =>
        selected.includes(id),
    ).length
    const isCurrentPageFullySelected =
        invoiceLength > 0 && currentPageSelectedCount === invoiceLength
    const isCurrentPagePartiallySelected =
        currentPageSelectedCount > 0 && currentPageSelectedCount < invoiceLength

    // For backend pagination, we need to determine the checkbox state based on:
    // 1. If allPagesSelected is true, checkbox should be checked
    // 2. If current page is fully selected, checkbox should be checked
    // 3. If there's any selection (current page or across pages), checkbox should be indeterminate
    const isChecked = allPagesSelected || isCurrentPageFullySelected
    const isIndeterminate =
        allPagesSelected || (selected.length > 0 && !isCurrentPageFullySelected)

    // Filter head cells based on visibleColumns prop
    const headCells = defaultHeadCells.filter((cell) =>
        visibleColumns.includes(cell.id),
    )

    const renderCellContent = (
        invoice: InternalInvoice,
        columnId: keyof InternalInvoice,
    ) => {
        switch (columnId) {
            case 'invoice_number':
                return invoice.invoice_number
            case 'billing_group_display_name':
                return (
                    invoice.billing_group.quickbooks_customer.display_name ||
                    'N/A'
                )
            case 'billing_email':
                return invoice.billing_group.invoice_email || 'N/A'
            case 'status':
                return (
                    <InvoiceStatusBadge
                        status={invoice.status}
                        voided={invoice.voided}
                        reviewed={invoice.reviewed}
                    />
                )
            case 'issue_date':
                return formatDate(invoice.issue_date)
            case 'due_date':
                return formatDate(invoice.due_date)
            case 'total_amount':
                return currencyFormatter.format(Number(invoice.total_amount))
            case 'balance_due':
                return currencyFormatter.format(Number(invoice.balance_due))
            default:
                return null
        }
    }

    return (
        <div className={classes.root}>
            <TableContainer component={Paper} className={classes.container}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {!hideSelectionColumn && (
                                <TableCell
                                    padding="checkbox"
                                    className={classes.headerCell}
                                >
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={isChecked}
                                        onChange={onSelectAll}
                                    />
                                </TableCell>
                            )}
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    sortDirection={
                                        orderBy === headCell.id ? order : false
                                    }
                                    className={classes.headerCell}
                                >
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={
                                            orderBy === headCell.id
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(headCell.id)}
                                    >
                                        {headCell.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell className={classes.headerCell}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ overflow: ' scroll' }}>
                        {invoices?.map((invoice) => {
                            return (
                                <TableRow
                                    key={invoice.id}
                                    className={
                                        invoice.voided
                                            ? classes.voidedRow
                                            : classes.normalRow
                                    }
                                    selected={selected.includes(invoice.id)}
                                    onClick={() => onSelectOne(invoice.id)}
                                    hover
                                >
                                    {!hideSelectionColumn && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selected.includes(
                                                    invoice.id,
                                                )}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                onChange={() =>
                                                    onSelectOne(invoice.id)
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={`${invoice.id}-${headCell.id}`}
                                        >
                                            {renderCellContent(
                                                invoice,
                                                headCell.id,
                                            )}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <IconButton
                                            onClick={(e) => {
                                                onViewInvoice(invoice)
                                                e.stopPropagation()
                                            }}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {(!invoices || invoices.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No invoices found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.pagination}
                component="div"
                count={totalCount}
                page={page}
                onChangePage={onPageChange}
                onChangeRowsPerPage={onRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </div>
    )
}
